import { Dialog } from 'radix-ui'
import type { ReactNode, PointerEvent as ReactPointerEvent } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from './Button.js'
import { useBackLayer } from './backStack.js'
import { cn } from './cn.js'
import { useKeyboardInset } from './useKeyboardInset.js'

/** Past this, the finger is dragging rather than pressing. */
const DRAG_START_PX = 6
/** Far enough that no scroll or mis-grab reaches it. */
const DISMISS_TRAVEL_PX = 96
/** A flick: short, but fast enough that it was clearly meant. */
const DISMISS_FLICK_PX = 28
const DISMISS_FLICK_SPEED = 0.55

export interface SheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  /** Hide the title visually while keeping it for screen readers. */
  hideTitle?: boolean
  description?: string
  children: ReactNode
  footer?: ReactNode
  /**
   * `full` fixes the height instead of letting it follow the content. A sheet
   * that searches has to: growing and shrinking under the results as they
   * arrive moves every row out from under the thumb that was reaching for it.
   */
  size?: 'auto' | 'full'
  /**
   * Work in progress. Closing asks first — by Back, by the close button, by the
   * overlay or by Escape, since all four lose the same typing.
   */
  dirty?: boolean
  className?: string
}

/**
 * Bottom sheet — the primary modal on a phone, because it opens next to the
 * thumb rather than in the middle of the screen.
 *
 * It sits above the keyboard rather than under it, owns a history entry so the
 * Back gesture closes it instead of the app, and can be flicked down by its
 * header. Padding respects the home indicator so the last control is never
 * under it.
 */
export function Sheet({
  open,
  onOpenChange,
  title,
  hideTitle = false,
  description,
  children,
  footer,
  size = 'auto',
  dirty = false,
  className,
}: SheetProps) {
  const keyboard = useKeyboardInset()
  const [asking, setAsking] = useState(false)
  const [drag, setDrag] = useState(0)
  const gesture = useRef<{
    id: number
    from: number
    at: number
    y: number
    speed: number
    captured: boolean
  } | null>(null)
  const scroller = useRef<HTMLDivElement | null>(null)

  const close = useCallback(() => onOpenChange(false), [onOpenChange])

  /** Every close path funnels here, so the question cannot be walked around. */
  const requestClose = useCallback(() => {
    if (dirty) {
      setAsking(true)
      return
    }
    close()
  }, [dirty, close])

  useBackLayer(open, close, { guard: () => dirty, onBlocked: () => setAsking(true) })

  // Asking is a property of an open sheet; a sheet closed from elsewhere while
  // the question is up would otherwise leave the question behind it.
  useEffect(() => {
    if (!open) setAsking(false)
  }, [open])

  /**
   * Keep the focused field in view as the keyboard arrives.
   *
   * Driven by the measured inset rather than by a timer after focus: guessing
   * how long the keyboard takes is what made this jump — too early and it
   * scrolls to a layout that is about to change, too late and the field is
   * hidden in between.
   */
  useEffect(() => {
    if (keyboard === 0) return
    const focused = document.activeElement
    if (!(focused instanceof HTMLElement)) return
    if (scroller.current?.contains(focused) !== true) return
    focused.scrollIntoView({ block: 'nearest' })
  }, [keyboard])

  const onPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    // The close button lives in the header too, and a press on it is a press.
    if (event.target instanceof Element && event.target.closest('button') !== null) return
    gesture.current = {
      id: event.pointerId,
      from: event.clientY,
      at: event.timeStamp,
      y: event.clientY,
      speed: 0,
      captured: false,
    }
  }

  const onPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    const current = gesture.current
    if (current === null || current.id !== event.pointerId) return
    const elapsed = event.timeStamp - current.at
    if (elapsed > 0) current.speed = (event.clientY - current.y) / elapsed
    current.y = event.clientY
    current.at = event.timeStamp
    // Downward only. Dragging a sheet up past its own top edge is a rubber band
    // with nothing behind it.
    const travelled = Math.max(0, event.clientY - current.from)
    // Capture only once this is unmistakably a drag. Capturing on the press
    // instead would retarget the click that follows a tap to this header, and
    // the close button inside it would stop working.
    if (!current.captured && travelled > DRAG_START_PX) {
      current.captured = true
      event.currentTarget.setPointerCapture(event.pointerId)
    }
    setDrag(travelled)
  }

  const onPointerUp = (event: ReactPointerEvent<HTMLElement>) => {
    const current = gesture.current
    if (current === null || current.id !== event.pointerId) return
    gesture.current = null
    const travelled = Math.max(0, event.clientY - current.from)
    setDrag(0)
    if (travelled > DISMISS_TRAVEL_PX) {
      requestClose()
      return
    }
    if (travelled > DISMISS_FLICK_PX && current.speed > DISMISS_FLICK_SPEED) requestClose()
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (next) {
          onOpenChange(true)
          return
        }
        requestClose()
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/45 data-[state=open]:animate-[fade-in_140ms_var(--ease-out-quick)]" />
        <Dialog.Content
          // Focus lands on the sheet itself, never on the first field inside
          // it. On a phone an auto-focused input throws up the keyboard, which
          // covers the two thirds of the sheet the user actually came to read;
          // typing starts when a field is tapped.
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            ;(event.currentTarget as HTMLElement | null)?.focus({ preventScroll: true })
          }}
          style={{
            bottom: keyboard,
            // The sheet is measured against what is left of the screen, so a
            // tall one shortens as the keyboard rises instead of running off
            // the top of it.
            maxHeight: `calc(92dvh - ${keyboard}px)`,
            height: size === 'full' ? `calc(92dvh - ${keyboard}px)` : undefined,
            transform: drag > 0 ? `translateY(${drag}px)` : undefined,
          }}
          className={cn(
            'fixed inset-x-0 z-50 flex flex-col overflow-hidden',
            'rounded-t-[var(--radius-sheet)] border-t border-line bg-surface',
            'data-[state=open]:animate-[sheet-up_180ms_var(--ease-out-quick)]',
            'focus:outline-none',
            drag === 0 && 'transition-transform duration-[180ms] ease-[var(--ease-out-quick)]',
            className,
          )}
        >
          {/* The whole header is the drag handle, not just the grabber: the bar
              is the sign, and a 4px sign would be a cruel target. Nothing here
              scrolls, so the gesture can never be the one the user meant for
              the list underneath. */}
          <header
            className="shrink-0 touch-none select-none border-b border-line px-4 pb-3 pt-2"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            <div className="mx-auto mb-2 h-1 w-9 rounded-[2px] bg-line-strong" />
            <div className="flex items-center justify-between gap-3">
              {hideTitle ? (
                <Dialog.Title className="sr-only">{title}</Dialog.Title>
              ) : (
                <Dialog.Title className="font-display text-lg font-semibold text-ink">
                  {title}
                </Dialog.Title>
              )}
              <button
                type="button"
                aria-label="Uždaryti"
                onClick={requestClose}
                // The before-layer stretches the 32px button to a 44px target.
                className="relative -mr-1 flex size-8 items-center justify-center rounded-[var(--radius-control)] text-mute transition-colors before:absolute before:-inset-2 before:content-[''] hover:bg-raised hover:text-ink"
              >
                <svg viewBox="0 0 16 16" className="size-4" fill="none" aria-hidden="true">
                  <path
                    d="M4 4l8 8M12 4l-8 8"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </header>
          {description != null && (
            <Dialog.Description className="shrink-0 px-4 pt-3 text-sm text-mute">
              {description}
            </Dialog.Description>
          )}
          <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            {children}
          </div>
          {footer != null && (
            <div
              className="shrink-0 border-t border-line px-4 py-3"
              // The home indicator is only in the way when nothing else is: with
              // the keyboard up the sheet already sits above both.
              style={{
                paddingBottom:
                  keyboard > 0 ? undefined : 'max(0.75rem, env(safe-area-inset-bottom))',
              }}
            >
              {footer}
            </div>
          )}

          {/* Asked inside the sheet rather than in a dialog of its own. A second
              modal over the first brings a second focus trap and a second scroll
              lock for one question, and the question is about this sheet — over
              it is where it belongs. */}
          {asking && (
            <div className="absolute inset-0 z-10 flex items-end bg-black/45 p-4">
              <div
                role="alertdialog"
                aria-label="Palikti neišsaugojus?"
                className="w-full rounded-[var(--radius-card)] border border-line bg-surface p-4"
              >
                <p className="font-display text-base font-semibold text-ink">
                  Palikti neišsaugojus?
                </p>
                <p className="mt-1 text-sm text-mute">Įvesti duomenys bus prarasti.</p>
                <div className="mt-4 flex gap-2">
                  {/* Focused, so the safe answer is the one a stray Enter gives. */}
                  <Button
                    block
                    autoFocus
                    onClick={() => {
                      setAsking(false)
                    }}
                  >
                    Grįžti
                  </Button>
                  <Button block variant="danger" onClick={close}>
                    Palikti
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
